import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  Pipe,
  PipeTransform,
} from '@angular/core';

import { TooltipModule } from 'primeng/tooltip';
import { NgClass } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { FieldModel, TableModel } from '../../../../models/extraction-models/extraction-models';
import {
  ExtractedRowForm,
  ExtractedTableCellForm,
} from '../../../../models/extraction-form-models';
import { ExtractedRow, TableReference } from '../../../../models/extracted-values';
import { EditTableLayoutComponent } from './edit-table-layout/edit-table-layout.component';
import { DropdownComponent } from './dropdown/dropdown.component';
import { TableItemInputComponent } from './table-item-input/table-item-input.component';
import { associate, associateWith, FieldType } from '@ps-gui/shared-utils';
import { FocusDirective } from '@ps-gui/shared-core';
import { DropdownFieldModel, DropdownOption } from '@ps-gui/ui';

function isStringArray(values: string[] | DropdownOption<string>[]): values is string[] {
  return values.every((x) => typeof x === FieldType.STRING);
}

@Pipe({
  name: 'inflate',
})
class InflatePipe implements PipeTransform {
  transform(values: string[] | DropdownOption<string>[]): Record<string, string> {
    if (isStringArray(values)) {
      return associateWith(values, (x) => x);
    }
    return associate(
      values,
      (item) => item.value,
      (item) => item.label,
    );
  }
}

@Component({
  selector: 'ct-extraction-table-row',
  imports: [
    TooltipModule,
    NgClass,
    ReactiveFormsModule,
    EditTableLayoutComponent,
    DropdownComponent,
    TableItemInputComponent,
    InflatePipe,
    FocusDirective,
  ],
  templateUrl: './table-row.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableRowComponent {
  readonly row = input.required<ExtractedRowForm>();
  readonly isTableLayoutEditable = input.required<boolean>();
  readonly currentRowToClassify = input.required<ExtractedRowForm | undefined>();

  readonly topAndBottomAddLineControls = input(false);
  readonly rowIndex = input(0);
  readonly isClassificationView = input(false);

  readonly currentTableFieldId = input<string>();

  readonly tableModel = input.required<TableModel>();
  readonly visibleFieldModels = input.required<FieldModel[]>();

  private readonly columnCount = computed(() => Object.keys(this.visibleFieldModels()).length);

  protected readonly isRowToClassify = computed(() => this.currentRowToClassify() === this.row());

  deleteRowAtIndex = output<number>();
  addRowAtIndex = output<{
    index: number;
    tableId: string | undefined;
  }>();

  newCurrentFieldIndex = output<number | undefined>();

  rowSelected = output<number | undefined>();
  outsideClick = output<void>();
  manualValueChange = output<{ fieldId: string }>();
  tableCallback = output<{
    currentRowForm: ExtractedRowForm;
    classValue: string | undefined;
  }>();

  protected onAddRow(index: number, row: ExtractedRow) {
    this.addRowAtIndex.emit({ index, tableId: (row.rowMembership as TableReference)?.tableId });
  }

  onUserInput(cellForm: ExtractedTableCellForm) {
    this.manualValueChange.emit({ fieldId: cellForm.controls.id.value });
  }

  onFieldSelected(rowIndex: number | undefined) {
    const fieldIndex = rowIndex !== undefined ? this.getFieldIndexInTable(rowIndex) : undefined;
    this.newCurrentFieldIndex.emit(fieldIndex);
  }

  private getFieldIndexInTable(fieldIndexInRow: number) {
    return this.rowIndex() * this.columnCount() + fieldIndexInRow;
  }

  // TODO we need any here because the inflate pipe expects other types than the actual type
  // options: Record<string, string>[];
  // pipe: transform(values: string[] | DropdownOption<string>[]): Record<string, string>
  getDropdownOptions(field: FieldModel) {
    return (field as DropdownFieldModel).options as any;
  }

  protected readonly FieldType = FieldType;
}
